import { getClientFullName } from "@zfp-akademie-a-s/contract-details";
import {
  type AnalysisClientItem,
  AnalysisClientItemAgeCategoryType,
  type ClientDetailResponse,
  type ClientDetailResponseClient,
  ClientRepresentativeType,
  ClientRepresentedType,
  ClientType,
  MangoClientCardStatus,
  type MangoRepresentativeDetail,
  type RelatedClientDetail,
  type SearchedClient,
  type SearchedLocalClient,
} from "Infrastructure/ApiZfp/Schema";
import { format, startOfDay, subYears } from "date-fns";

/**
 * `useClientDetail` newly returns `{ client, references }` - unwraps it back to the client itself,
 * so call sites that only need the client keep working unchanged.
 *
 * Kept as a module-level constant on purpose: an inline arrow changes identity on every render,
 * which would make React Query re-run the selector and deep-compare the result each time.
 *
 * Note: with a `select`, the `queryKey` returned from the hook carries the *selected* type, while
 * the cache holds the whole `ClientDetailResponse` - don't read the cache through that tagged key.
 */
export const selectClientDetailClient = (response: ClientDetailResponse): ClientDetailResponseClient => response.client;

/**
 * `GET /v1/clients/{guid}` legitimately 404s - a client picked from the search doesn't have to exist
 * on the other side. React Query's default of three attempts only stretches the wait before the error
 * state shows; a single retry still covers a one-off network blip.
 */
export const CLIENT_DETAIL_RETRY = 1;

/** Local / Mango client detail, i.e. everything except the related-person variant. */
export type NonRelatedClientDetail = Exclude<ClientDetailResponseClient, RelatedClientDetail>;

export enum ClientCardState {
  NotInMango = "notInMango",
  Missing = "missing",
  Unsigned = "unsigned",
  Outdated = "outdated",
  Actual = "actual",
}

/**
 * `cardStatus` only singles out the missing card - signed vs. unsigned still comes from `signed`, so a detail
 * without `cardStatus` keeps its previous state instead of falling through to a wrong one.
 */
export const getClientCardState = (client: ClientDetailResponseClient): ClientCardState => {
  if (client.type !== ClientType.existing) return ClientCardState.NotInMango;
  if (client.cardStatus === MangoClientCardStatus.missing) return ClientCardState.Missing;
  if (!client.signed) return ClientCardState.Unsigned;
  return client.verified ? ClientCardState.Actual : ClientCardState.Outdated;
};

/** A guardian or an attorney isn't a legal representative, so they don't count here. */
export const getLegalRepresentative = (client: ClientDetailResponseClient): MangoRepresentativeDetail | undefined => {
  if (!("representation" in client)) return undefined;
  const { representation } = client;
  if (representation.type !== ClientRepresentedType.represented) return undefined;
  return representation.representativeType === ClientRepresentativeType.legal_representative ? representation.representative : undefined;
};

/** A prefix alone isn't a phone number - without the national number there is nothing to show. */
export const formatClientPhone = (prefix: string | null | undefined, nationalNumber: string | null | undefined): string | undefined =>
  nationalNumber ? `${prefix ?? ""}${nationalNumber}` : undefined;

export const formatClientName = (firstName: string, lastName: string, birthDate: string | Date | null): string => {
  if (birthDate) return `${firstName} ${lastName} (${format(birthDate as unknown as Date, "dd.MM.yyyy")})`;
  return `${firstName} ${lastName}`;
};

export const MAX_CLIENT_AGE_YEARS = 100;

/** Catches a mistyped year (945 instead of 1945) - the date field itself accepts any year. */
export const getMinBirthDate = (): Date => subYears(startOfDay(new Date()), MAX_CLIENT_AGE_YEARS);

export const isPlannedChild = (client: Pick<AnalysisClientItem, "ageCategoryType">): boolean =>
  client.ageCategoryType === AnalysisClientItemAgeCategoryType.plannedChild;

/**
 * Rodné číslo comes from the API both with and without the slash - normalize the display form
 * by inserting it after the six-digit date part. Numbers assigned before 1954 are 9 digits with a
 * three-digit suffix, later ones 10 digits with a four-digit suffix. Anything else (already
 * slashed, or an unexpected format) is left untouched.
 */
export const formatPersonalIdentificationNumber = (personalIdentificationNumber: string | null | undefined): string | null | undefined => {
  if (!personalIdentificationNumber || !/^\d{9,10}$/.test(personalIdentificationNumber)) return personalIdentificationNumber;

  const suffixLength = personalIdentificationNumber.length === 9 ? 3 : 4;
  return `${personalIdentificationNumber.slice(0, -suffixLength)}/${personalIdentificationNumber.slice(-suffixLength)}`;
};

/**
 * Parses a Czech/Slovak rodné číslo written with or without the slash, or returns `null` when it isn't valid.
 *
 * Numbers issued before 1954 are 9 digits with no check digit, later ones 10 digits divisible by 11 - except
 * 1954-1985 numbers whose first nine digits leave remainder 10, which carry a check digit of 0. The month may
 * carry +50 (women) and, from 2004 on, +20.
 *
 * `birthDate` is the date encoded in the number, or `null` when it doesn't exist in the calendar (31 February) -
 * such a number still counts as valid, the day is only checked to be 1-31.
 */
export const parseBirthNumber = (
  input: string | null | undefined,
): { digits: string; formatted: string; birthDate: Date | null } | null => {
  const match = /^(\d{6})\/?(\d{3,4})$/.exec((input ?? "").replace(/\s/g, ""));
  if (!match) return null;

  const [, datePart, suffix] = match;
  const digits = `${datePart}${suffix}`;
  const twoDigitYear = Number(digits.slice(0, 2));
  const year = digits.length === 9 || twoDigitYear >= 54 ? 1900 + twoDigitYear : 2000 + twoDigitYear;
  const month = Number(digits.slice(2, 4)) % 50;
  const day = Number(digits.slice(4, 6));

  const plainMonth = month > 20 && year >= 2004 ? month - 20 : month;
  if (plainMonth < 1 || plainMonth > 12 || day < 1 || day > 31) return null;

  if (digits.length === 9) {
    if (year >= 1954) return null;
  } else {
    const remainder = Number(digits.slice(0, 9)) % 11;
    const checkDigit = Number(digits[9]);
    if (remainder === 10 ? year > 1985 || checkDigit !== 0 : remainder !== checkDigit) return null;
  }

  const birthDate = new Date(year, plainMonth - 1, day);
  return { digits, formatted: `${datePart}/${suffix}`, birthDate: birthDate.getDate() === day ? birthDate : null };
};

const getFirstLetter = (name: string | null | undefined): string => {
  const firstCodePoint = name?.trim().codePointAt(0);
  return firstCodePoint === undefined ? "" : String.fromCodePoint(firstCodePoint).toUpperCase();
};

type NameInitialsInput = {
  readonly firstName: string | null | undefined;
  readonly surname: string | null | undefined;
  readonly fullName?: string;
};

/**
 * Initials for an avatar: one letter from the first given name and one from the first surname,
 * so "Jan Karel" + "Novák Svoboda" is still "JN".
 *
 * `fullName` is only a fallback for when neither part is known - a composed name can't be split into
 * given names and surnames reliably, so its first and last word are the best guess.
 */
export const getNameInitials = ({ firstName, surname, fullName }: NameInitialsInput): string => {
  const initials = getFirstLetter(firstName) + getFirstLetter(surname);
  if (initials || !fullName) return initials;

  const words = fullName.trim().split(/\s+/);
  return getFirstLetter(words[0]) + (words.length > 1 ? getFirstLetter(words[words.length - 1]) : "");
};

/**
 * Display name of a client search hit: first name + surname when the API sends them separately,
 * falling back to the already-composed `name` field when it doesn't.
 */
export const getSearchedClientName = (client: SearchedClient | SearchedLocalClient): string =>
  client.firstName || client.surname ? getClientFullName({ name: client.firstName ?? "", surname: client.surname ?? "" }) : client.name;
